/**
 * Glyph format and path helpers for the Eguchi Homes icon set (see the spec at
 * the top of lib/icons.tsx). Glyphs are plain data (SVG path strings), so the
 * set costs a few bytes per icon instead of a JSX tree each.
 */

/**
 * One icon.
 * - `d`: the line drawing, stroked with currentColor at the set's 1.75px weight.
 * - `g`: soft "active" fill (currentColor at 28%): lit shade, open doorway, daylight.
 * - `f`: solid "active" fill (currentColor): battery charge, a pilot dot.
 * - `x`: faint strokes (35%) for the unreached steps of a level (Wi-Fi, signal).
 * - `spin`: strokes that rotate about `o` (default 12 12) when motion is allowed.
 */
export interface GlyphDef {
  d: string;
  g?: string;
  f?: string;
  x?: string;
  spin?: string;
  o?: string;
}
export type Glyph = string | GlyphDef;

const n = (v: number): string => String(Math.round(v * 100) / 100);

/** Circle as a path. */
export const c = (cx: number, cy: number, r: number): string =>
  `M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 0 ${n(2 * r)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-2 * r)} 0z`;

/** Rounded rectangle as a path. */
export const rr = (x: number, y: number, w: number, h: number, r: number): string => {
  const a = (dx: number, dy: number) => `a${n(r)} ${n(r)} 0 0 1 ${n(dx)} ${n(dy)}`;
  return `M${n(x + r)} ${n(y)}h${n(w - 2 * r)}${a(r, r)}v${n(h - 2 * r)}${a(-r, r)}h${n(-(w - 2 * r))}${a(-r, -r)}v${n(-(h - 2 * r))}${a(r, -r)}z`;
};

/** A dot drawn by the round cap of a zero-length stroke (diameter = stroke width). */
export const dot = (x: number, y: number): string => `M${n(x)} ${n(y)}h.01`;
